// Product analytics + feature flags for the web app (PostHog, 4.0.1 ops). Off unless the api's GET /api/config carries
// a `posthog` block (POSTHOG_KEY + POSTHOG_WEB=1 on the api), and never in mock mode. Most events are sent by the api
// (api/src/analytics.ts); the browser adds only what only it sees: an error shown to the user (its code, nothing else).
// Privacy (docs/product/analytics.md):
// - starts only once someone is signed in, and is identified by our user id alone (no email, no name)
// - off entirely when the browser sends Do Not Track or Global Privacy Control
// - no cookies (localStorage persistence), no autocapture, no pageviews, no session replay, no surveys, no heatmaps,
//   and the no-external build, so it never loads more scripts
// - every event's URL is cut to its route shape (/p/:id) with no query string; referrers are dropped
// The SDK is its own lazy chunk, loaded only when all of that says yes.

import type { PostHogConfig } from "posthog-js";

export type WebAnalyticsConfig = { key: string; host: string; environment: string };

/** Events the browser sends (the api sends the rest; api/src/analytics.ts EVENTS). */
export const WEB_EVENTS = ["error_shown"] as const;
export type WebEvent = (typeof WEB_EVENTS)[number];

/** The part of posthog-js we use (tests pass a fake). */
export type PosthogLike = {
  init(key: string, config: Partial<PostHogConfig>): unknown;
  identify(id: string): void;
  capture(event: string, props?: Record<string, unknown>): unknown;
  isFeatureEnabled(name: string): boolean | undefined;
  reset(): void;
};

/** The `posthog` block of /api/config, checked: anything malformed (or a non-project key) means off. */
export function parsePosthogConfig(raw: unknown): WebAnalyticsConfig | null {
  const p = (raw as { posthog?: unknown } | null)?.posthog as Record<string, unknown> | undefined;
  if (!p || typeof p.key !== "string" || !/^phc_[A-Za-z0-9_-]{10,}$/.test(p.key.trim())) return null;
  if (typeof p.host !== "string" || !/^https:\/\/[a-z0-9.-]+(:\d+)?\/?$/i.test(p.host.trim())) return null;
  return { key: p.key.trim(), host: p.host.trim().replace(/\/+$/, ""), environment: typeof p.environment === "string" && p.environment ? p.environment : "production" };
}

type Nav = { doNotTrack?: string | null; globalPrivacyControl?: boolean; msDoNotTrack?: string | null } | undefined;
type Win = { doNotTrack?: string | null } | undefined;

/** Do Not Track or Global Privacy Control: the browser asked not to be tracked, so we don't start at all. */
export function privacyOptOut(nav: Nav = typeof navigator !== "undefined" ? (navigator as Nav) : undefined, win: Win = typeof window !== "undefined" ? (window as Win) : undefined): boolean {
  const dnt = nav?.doNotTrack ?? nav?.msDoNotTrack ?? win?.doNotTrack ?? null;
  return dnt === "1" || dnt === "yes" || nav?.globalPrivacyControl === true;
}

// ------------------------------------------------------------------ URL scrubbing (pure; tested)

/** A path cut to its route shape: ids, invite codes and share tokens become ":id". */
export function routeShape(pathname: string): string {
  const parts = pathname.split("/").map((seg, i, all) => {
    if (i === 0 || !seg) return seg;
    const prev = all[i - 1];
    if (prev && ["p", "c", "k", "s", "cast", "invite", "share"].includes(prev)) return ":id";
    if (/^[0-9a-f-]{16,}$/i.test(seg) || /\d{3,}/.test(seg)) return ":id";
    return seg;
  });
  return parts.join("/") || "/";
}

/** A URL with no query string or fragment, its path cut to the route shape. Anything unparseable: dropped. */
export function scrubUrl(u: unknown): string | null {
  if (typeof u !== "string" || !u) return null;
  try {
    const x = new URL(u);
    return `${x.origin}${routeShape(x.pathname)}`;
  } catch {
    return null;
  }
}

const URL_PROPS = ["$current_url", "$initial_current_url", "$session_entry_url"];
const PATH_PROPS = ["$pathname", "$initial_pathname", "$session_entry_pathname"];
const DROP_PROPS = ["$referrer", "$referring_domain", "$initial_referrer", "$initial_referring_domain", "$session_entry_referrer", "$session_entry_referring_domain", "$search_engine", "$initial_search_engine"];

type CaptureLike = { event?: string; properties?: Record<string, unknown>; $set?: Record<string, unknown>; $set_once?: Record<string, unknown> } | null;

/** before_send: scrubs URLs and drops referrers on the event and on any person properties it sets. */
export function scrubCapture<T extends CaptureLike>(e: T): T {
  if (!e) return e;
  for (const bag of [e.properties, e.$set, e.$set_once]) {
    if (!bag) continue;
    for (const k of URL_PROPS) if (k in bag) bag[k] = scrubUrl(bag[k]);
    for (const k of PATH_PROPS) if (typeof bag[k] === "string") bag[k] = routeShape(bag[k] as string);
    for (const k of DROP_PROPS) delete bag[k];
  }
  return e;
}

// ------------------------------------------------------------------ the client

type Loader = () => Promise<PosthogLike>;
const defaultLoader: Loader = () => import("posthog-js/no-external").then((m) => m.default as unknown as PosthogLike);

let cfg: WebAnalyticsConfig | null = null;
let ph: PosthogLike | null = null;
let starting: Promise<PosthogLike | null> | null = null;
let userId: string | null = null;
let loader: Loader = defaultLoader;
let sink: ((e: { event: WebEvent; props: Record<string, unknown> }) => void) | null = null;

/** Tests: a fake SDK, and a sink that sees every event. */
export function _setForTests(o: { loader?: Loader | null; sink?: typeof sink }) {
  if (o.loader !== undefined) loader = o.loader ?? defaultLoader;
  if (o.sink !== undefined) sink = o.sink;
}
/** Tests: back to off. */
export function _resetForTests() {
  cfg = null;
  ph = null;
  starting = null;
  userId = null;
}

/** The posthog-js settings: everything that could collect more than our events is off. */
export function posthogOptions(c: WebAnalyticsConfig): Partial<PostHogConfig> {
  return {
    api_host: c.host,
    person_profiles: "identified_only",
    persistence: "localStorage", // no cookies
    autocapture: false,
    capture_pageview: false,
    capture_pageleave: false,
    disable_session_recording: true,
    disable_surveys: true,
    capture_exceptions: false, // Sentry reports errors
    capture_performance: false,
    capture_heatmaps: false,
    capture_dead_clicks: false,
    enable_heatmaps: false,
    rageclick: false,
    respect_dnt: true,
    mask_all_text: true,
    mask_all_element_attributes: true,
    disable_external_dependency_loading: true,
    save_referrer: false,
    property_denylist: DROP_PROPS,
    before_send: (e) => scrubCapture(e),
  };
}

/** Called with /api/config's block once it loads. With none (or DNT / GPC, or mock mode) analytics stays off. */
export function configureAnalytics(c: WebAnalyticsConfig | null) {
  cfg = c && !privacyOptOut() && import.meta.env?.VITE_MOCK !== "1" ? c : null;
  void syncUser();
}

/** The signed-in user's id (null when signed out). PostHog starts at the first id and is reset on sign-out. */
export function setAnalyticsUser(id: string | null) {
  if (id === userId) return;
  userId = id;
  void syncUser();
}

async function syncUser() {
  try {
    if (!userId) {
      ph?.reset();
      return;
    }
    const p = await start();
    if (p && userId) p.identify(userId);
  } catch {
    /* analytics must never break the app */
  }
}

function start(): Promise<PosthogLike | null> {
  if (!cfg) return Promise.resolve(null);
  const c = cfg;
  starting ??= loader()
    .then((p) => {
      p.init(c.key, posthogOptions(c));
      ph = p;
      return p;
    })
    .catch(() => null); // a blocked chunk (an ad blocker) leaves it off
  return starting;
}

/** Sends one web event for the signed-in user. Never throws; nothing while off or signed out. */
export function track(event: WebEvent, props: Record<string, string | number | boolean | null> = {}) {
  try {
    if (!cfg || !userId) return;
    sink?.({ event, props });
    ph?.capture(event, { ...props, environment: cfg.environment, source: "web" });
  } catch {
    /* never break the app */
  }
}

/**
 * Is feature flag `name` on for this user? `dflt` while PostHog is off, still loading its flags, or doesn't know the
 * flag. Synchronous: read it when rendering; flags change only on reload.
 */
export function flag(name: string, dflt: boolean): boolean {
  try {
    const v = ph?.isFeatureEnabled(name);
    return typeof v === "boolean" ? v : dflt;
  } catch {
    return dflt;
  }
}

// ------------------------------------------------------------------ errors shown (code only)

let lastCode: { code: string; at: number } | null = null;

/** api.ts notes each ApiError's code; the next "bad" toast within a few seconds reports it. */
export function noteErrorCode(code: string | null, status: number) {
  lastCode = { code: code && /^[A-Z0-9_]{2,40}$/.test(code) ? code : status > 0 ? `HTTP_${status}` : "NETWORK", at: Date.now() };
}

/** An error toast was shown: sends error_shown with the api's code (or APP when it didn't come from the api). */
export function errorShown(now = Date.now()) {
  const code = lastCode && now - lastCode.at < 5000 ? lastCode.code : "APP";
  lastCode = null;
  track("error_shown", { code });
}
